import React, { useState } from 'react';
import { Star, Zap, X, Check, ExternalLink } from 'lucide-react';
import { ImageEdit } from "@/components/editor/ImageEdit";
import type { Canvas as FabricCanvas } from "fabric/fabric-impl";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAppSelector } from '@/hooks/useAppSelector';
import { Badge } from "@/components/ui/badge";
import { getEventLabelsFromEvent, EVENT_CHANGES } from '@/utils/eventLabels';
import { PricingEvent } from '@/store/eventsSlice';
import { formatVersionDateUS } from '@/utils/dateUtils';

interface EventSwipeCardProps {
  company: PricingEvent;
  onToggleProject: () => Promise<void>;
  isInProject: boolean;
  fabricJson?: any;
  fabricJson2?: any;
  entryId?: string;
  projectId?: string;
  onScrapbookUpdate?: () => Promise<void>;
}

const EventSwipeCard = ({
  company,
  onToggleProject,
  isInProject,
  fabricJson,
  fabricJson2,
  entryId,
  projectId,
  onScrapbookUpdate
}: EventSwipeCardProps) => {
  const [isEditingImage1, setIsEditingImage1] = useState(false);
  const [isEditingImage2, setIsEditingImage2] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(company.description || '');
  // Project functionality removed
  const eventLabels = getEventLabelsFromEvent(company);

  const formatVersionDate = (version: string | null) => {
    return formatVersionDateUS(version) || '';
  };

  const handleCanvasChange = async (canvas: FabricCanvas, isSecondImage: boolean) => {
    const effectiveProjectId = projectId || selectedProject?.id;
    
    if (!effectiveProjectId || !entryId) {
      console.error('No project ID or entry ID available');
      return;
    }

    try {
      const fabricData = canvas.toJSON(['id', 'selectable']);
      const jsonData = JSON.parse(JSON.stringify(fabricData));

      const { error } = await supabase
        .from('scrapbook_entries')
        .update({
          [isSecondImage ? 'fabric_json2' : 'fabric_json']: jsonData
        })
        .eq('id', entryId);

      if (error) throw error;

        if (isSecondImage) {
          setIsEditingImage2(false);
        } else {
          setIsEditingImage1(false);
        }
      toast.success('Changes saved successfully');

      if (onScrapbookUpdate) {
        await onScrapbookUpdate();
      }
    } catch (error) {
      console.error('Error saving canvas:', error);
      toast.error('Failed to save changes');
    }
  };

  const handleReset = async (isSecondImage: boolean) => {
    if (!entryId) {
      console.error('No entry ID available');
      return;
    }

    try {
      const { error } = await supabase
        .from('scrapbook_entries')
        .update({
          [isSecondImage ? 'fabric_json2' : 'fabric_json']: null
        })
        .eq('id', entryId);

      if (error) throw error;

      toast.success('Image reset successfully');
        if (isSecondImage) {
          setIsEditingImage2(false);
        } else {
          setIsEditingImage1(false);
        }
    } catch (error) {
      console.error('Error resetting image:', error);
      toast.error('Failed to reset image');
    }
  };

  const handleSaveDescription = async () => {
    if (!entryId) {
      console.error('No entry ID available');
      return;
    }

    try {
      const { error } = await supabase
        .from('scrapbook_entries')
        .update({ description: editedDescription })
        .eq('id', entryId);

      if (error) throw error;

      toast.success('Description updated successfully');
      setIsEditingDescription(false);
    } catch (error) {
      console.error('Error updating description:', error);
      toast.error('Failed to update description');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative w-12 h-12 flex-shrink-0">
            {company.logoUrl && (
              <img
                src={company.logoUrl}
                alt={`${company.slug} logo`}
                className="w-full h-full object-contain"
              />
            )}
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border-2 border-white">
              <Zap className="h-4 w-4 text-yellow-500" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg text-gray-900 capitalize">
                  {company.slug.replace(/-/g, ' ')}
                </h3>
              </div>
            </div>
            <div onClick={() => !isEditingDescription && setIsEditingDescription(true)} className="mt-1 flex-1 pr-12">
              {isEditingDescription ? (
                <div className="space-y-2">
                  <Textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Add a description..."
                    className="w-full"
                    autoFocus
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditedDescription(company.description || '');
                        setIsEditingDescription(false);
                      }}
                      className="gap-1"
                    >
                      <X className="h-3 w-3" />
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleSaveDescription}
                      className="gap-1"
                    >
                      <Check className="h-3 w-3" />
                      Save
                    </Button>
                  </div>
                </div>
              ) : company.description ? (
                <p className="text-sm text-gray-600 cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded">
                  {company.description}
                </p>
              ) : (
                <p className="text-sm text-gray-400 italic cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded">
                  Click to add a description...
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {eventLabels.map((label, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className={`flex items-center gap-1 ${label.className}`}
                >
                  {label.icon && <label.icon className="h-3 w-3" />}
                  {label.label}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <button
          onClick={onToggleProject}
          className={`p-2 rounded-full transition-colors flex-shrink-0 ${
            isInProject
              ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-600'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-400'
          }`}
          title={isInProject ? `Remove from project` : `Add to project`}
        >
          <Star className={`h-5 w-5 ${isInProject ? 'fill-current' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-sm text-gray-600 font-bold text-center">
            {formatVersionDate(company.v1)}
          </div>
          <div
            className="relative group"
            onClick={() => !isEditingImage1 && setIsEditingImage1(true)}
          >
            <ImageEdit
              slug={`${company.slug}-before`}
              screenshotUrl={company.beforeUrl}
              fabricJson={fabricJson}
              onSave={(canvas) => handleCanvasChange(canvas, false)}
              onCancel={() => setIsEditingImage1(false)}
              onReset={() => handleReset(false)}
              className="h-[450px]"
              isEditMode={isEditingImage1}
            />
            {!isEditingImage1 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors cursor-pointer rounded-lg">
                <p className="text-gray-700 bg-white/90 px-4 py-2 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  Click to Annotate
                </p>
              </div>
            )}
          </div>
          <a
            href={company.beforeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors mt-2"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3 w-3" />
            View Original Image
          </a>
        </div>
        <div className="space-y-2">
          <div className="text-sm text-gray-600 font-bold text-center">
            {formatVersionDate(company.v2)}
          </div>
          <div
            className="relative group"
            onClick={() => !isEditingImage2 && setIsEditingImage2(true)}
          >
            <ImageEdit
              slug={`${company.slug}-after`}
              screenshotUrl={company.afterUrl}
              fabricJson={fabricJson2}
              onSave={(canvas) => handleCanvasChange(canvas, true)}
              onCancel={() => setIsEditingImage2(false)}
              onReset={() => handleReset(true)}
              className="h-[450px]"
              isEditMode={isEditingImage2}
            />
            {!isEditingImage2 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors cursor-pointer rounded-lg">
                <p className="text-gray-700 bg-white/90 px-4 py-2 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  Click to Annotate
                </p>
              </div>
            )}
          </div>
          <a
            href={company.afterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors mt-2"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3 w-3" />
            View Original Image
          </a>
        </div>
      </div>
    </div>
  );
};

export default EventSwipeCard;
